import React from 'react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header Section */}
      <div className="max-w-4xl mx-auto pt-20 pb-16 px-5 text-center">
        <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
          Terms and Conditions
        </h1>
        <div className="w-24 h-1 bg-primary mx-auto rounded-full mb-6"></div>
        <p className="text-lg text-gray-600 mb-4">
          Please read these terms carefully before using TravelMaker
        </p>
        <p className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-full inline-block">
          Last Updated: August 1, 2023
        </p>
      </div>

      {/* Content Section */}
      <div className="max-w-4xl mx-auto px-5 pb-20">
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">1. Acceptance of Terms</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              By downloading, installing, or using the TravelMaker application ("App"), you agree to be bound by these 
              Terms and Conditions ("Terms"). If you do not agree to these Terms, you must not access or use the App.
            </p>
            <p>
              TravelMaker reserves the right to change or modify these Terms at any time. Your continued use of the App 
              following the posting of any changes constitutes your acceptance of such changes.
            </p>
          </div>
        </section>
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">2. Use of the App</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              TravelMaker grants you a limited, non-exclusive, non-transferable, revocable license to use the App for your 
              personal, non-commercial purposes. You may not:
            </p>
            <ul className="ml-6 space-y-2 list-disc">
              <li>Use the App for any illegal purpose or in violation of any local, state, national, or international law</li>
              <li>Copy, modify, adapt, translate, or create derivative works based on the App</li>
              <li>Attempt to reverse engineer, decompile, or disassemble the App</li>
              <li>Remove, circumvent, disable, damage, or otherwise interfere with security-related features of the App</li>
              <li>Use automated scripts to collect information from or interact with the App</li>
              <li>Impersonate another person or entity, or falsely state or misrepresent your affiliation with a person or entity</li>
            </ul>
          </div>
        </section>
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">3. User Accounts</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              To use certain features of the App, you may be required to create a user account. You are responsible for:
            </p>
            <ul className="ml-6 space-y-2 list-disc">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying TravelMaker immediately of any unauthorized use of your account</li>
            </ul>
            <p>
              TravelMaker reserves the right to disable your account at any time if, in our opinion, you have failed to comply 
              with any provision of these Terms.
            </p>
          </div>
        </section>
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">4. User Content</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              The App may allow you to post, link, store, share, or otherwise make available certain information, text, graphics, 
              videos, or other material ("User Content"). You retain all rights in, and are solely responsible for, the User 
              Content you post.
            </p>
            <p>
              By posting User Content, you grant TravelMaker a worldwide, non-exclusive, royalty-free license to use, reproduce, 
              modify, adapt, publish, translate, distribute, and display such content in connection with providing the App.
            </p>
            <p>
              You represent and warrant that:
            </p>
            <ul className="ml-6 space-y-2 list-disc">
              <li>You own or have the necessary rights to use and authorize TravelMaker to use your User Content</li>
              <li>Your User Content does not violate the privacy rights, publicity rights, copyright rights, or other rights of any person</li>
            </ul>
          </div>
        </section>
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">5. Intellectual Property</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              The App and its original content, features, and functionality are and will remain the exclusive property of 
              TravelMaker and its licensors. The App is protected by copyright, trademark, and other laws of both the United 
              States and foreign countries.
            </p>
            <p>
              Our trademarks and trade dress may not be used in connection with any product or service without the prior 
              written consent of TravelMaker.
            </p>
          </div>
        </section>
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">6. Third-Party Links and Content</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              The App may contain links to third-party websites or services that are not owned or controlled by TravelMaker.
            </p>
            <p>
              TravelMaker has no control over, and assumes no responsibility for, the content, privacy policies, or practices 
              of any third-party websites or services. You further acknowledge and agree that TravelMaker shall not be 
              responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in 
              connection with the use of or reliance on any such content, goods, or services available on or through any such 
              websites or services.
            </p>
          </div>
        </section>
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">7. Limitation of Liability</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              In no event shall TravelMaker, its directors, employees, partners, agents, suppliers, or affiliates, be liable 
              for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of 
              profits, data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="ml-6 space-y-2 list-disc">
              <li>Your access to or use of or inability to access or use the App</li>
              <li>Any conduct or content of any third party on the App</li>
              <li>Any content obtained from the App</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
            </ul>
          </div>
        </section>
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">8. Governing Law</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              These Terms shall be governed and construed in accordance with the laws of the State of California, without 
              regard to its conflict of law provisions.
            </p>
            <p>
              Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. 
              If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of 
              these Terms will remain in effect.
            </p>
          </div>
        </section>
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">9. Contact Us</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <p>
                <strong>Email:</strong> <a href="mailto:info@travelmaker.app" className="text-primary hover:text-primary/80">info@travelmaker.app</a><br />
                <strong>Address:</strong> London, United Kingdom
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Terms;